import { Injectable, Injector } from '@angular/core';
import { FieldTypeConfig, FormlyFieldConfig } from '@ngx-formly/core';
import { AbstractRender } from 'src/app/abstract/render.abstract';
import { WidgetsAbstract } from 'src/app/abstract/widgets/widgets.abstract';
import { Logger } from 'src/app/core/logger';
import { WidgetsTimeRangeModel } from '../../models/widgets/widgets-time-range.model';
import { CloneService } from '../clone/clone.service';

@Injectable({
  providedIn: 'root',
})
export class RenderTimeRangeService extends AbstractRender {
  constructor(private injector: Injector, private logger: Logger) {
    super();
  }
  /**
   * Recursive render method
   *
   * @param timeRange A node
   * @param config Formly fieldTo config array
   * @param widgetRoot Root of the layout
   * @param formlyRoot Root of the FormlyFieldConfig
   * @param isInsideQuestion True if we are rendering a subtree inside a question
   * @param isRecursive True if we want to traverse the tree to the bottom. False if we just want to render a node.
   */
  render(
    widget: WidgetsAbstract,
    config: FormlyFieldConfig[],
    widgetRoot: WidgetsAbstract,
    formlyRoot: FormlyFieldConfig,
    isInsideQuestion: boolean,
    isRecursive: boolean
  ) {
    const timeRangeModel = widget as WidgetsTimeRangeModel;
    const label: FormlyFieldConfig = {};

    if (timeRangeModel.type !== 'timeRange') {
      return;
    }

    // Override labels on Fields
    // Go up to custom model and overrides labels
    const labelOverrides: Map<string, string> = new Map<string, string>();
    CloneService.getLabelOverrides(timeRangeModel, widgetRoot, labelOverrides);
    const overridedString = CloneService.overrideText(
      timeRangeModel.label,
      labelOverrides
    );

    let fieldGroupPrimitive: FormlyFieldConfig = {
      className: 'primitive-timerange',
      fieldGroupClassName: 'primitive-timerange',
      fieldGroup: []
    };
    if (timeRangeModel.description) {
      fieldGroupPrimitive.props = {
        description: timeRangeModel.description,
      };
    }

    // Mix incoming template with the group
    if (timeRangeModel.template) {
      const modelTemplate = JSON.parse(timeRangeModel.template);
      fieldGroupPrimitive = Object.assign({}, fieldGroupPrimitive, modelTemplate);
    }

    // 1. From
    const fieldFrom: FormlyFieldConfig = {};
    fieldFrom.key = timeRangeModel.key + timeRangeModel.suffixFrom;
    fieldFrom.type = 'uw-input';
    fieldFrom.className = 'prop-from';
    fieldFrom.props = {
      label: timeRangeModel.label && overridedString ? overridedString : 'From:',
      type: 'time',
      required: timeRangeModel.required
    };

    // 2. To
    const fieldTo: FormlyFieldConfig = {};
    fieldTo.key = timeRangeModel.key + timeRangeModel.suffixTo;
    fieldTo.type = 'uw-input';
    fieldTo.className = 'prop-to';
    fieldTo.props = {
      label: timeRangeModel.label && overridedString ? overridedString : 'To:',
      type: 'time',
      required: timeRangeModel.required,
    };


    // 3. Days
    const fieldDays: FormlyFieldConfig = {};
    fieldDays.key = timeRangeModel.key + timeRangeModel.suffixDays;
    fieldDays.type = 'uw-input';
    fieldDays.className = 'prop-days';
    fieldDays.props = {
      label: 'Days:',
      type: 'uw-input',
      required: false,
      maxLength: 35
    };
    fieldDays.props.required = timeRangeModel.required ? timeRangeModel.required : false;
    fieldDays.validators = { validation: ['trimValidation'] };
    fieldDays.props.required = timeRangeModel.required;

    // Require validation condition for row2 and row3 for Days column
    if (!timeRangeModel.required) {
      fieldDays.expressions = {
        // https://stackoverflow.com/questions/62438346/how-to-dynamically-access-object-property-in-typescript
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'props.required': (field: FormlyFieldConfig) => {
          const keyValueTo = fieldTo.key as keyof typeof field;
          const keyValueFrom = fieldFrom.key as keyof typeof field;
          return field.model[keyValueTo] as boolean || field.model[keyValueFrom] as boolean;
        }
      };
    }

    // Require validation condition for row2 and row3 for To column
    if (!timeRangeModel.required) {
      fieldTo.expressions = {
        // https://stackoverflow.com/questions/62438346/how-to-dynamically-access-object-property-in-typescript
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'props.required': (field: FormlyFieldConfig) => {
          const keyValueFrom = fieldFrom.key as keyof typeof field;
          const keyValueDays = fieldDays.key as keyof typeof field;
          return field.model[keyValueFrom] as boolean || field.model[keyValueDays] as boolean;
        }
      };
    }

    // Require validation condition for row2 and row3 for from column
    if (!timeRangeModel.required) {
      fieldFrom.expressions = {
        // https://stackoverflow.com/questions/62438346/how-to-dynamically-access-object-property-in-typescript
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'props.required': (field: FormlyFieldConfig) => {
          const keyValueTo = fieldTo.key as keyof typeof field;
          const keyValueDays = fieldDays.key as keyof typeof field;
          return field.model[keyValueTo] as boolean || field.model[keyValueDays] as boolean;
        }
      };
    }

    // Validation
    for (const validation of timeRangeModel.children) {
      try {
        const valTemplate: string = validation.getTemplate();
        if (valTemplate) {
          const template = JSON.parse(valTemplate);
          fieldDays.validators = {
            validation: [template],
          };
          // FIXME: Check if validation is only for fieldTo or for form...
          if (template.name === 'timeRange') {
            // else if it is for whole form...
            const validators = { validators: { validation: [template] } };

            // FIXME: For now apply at the root level for date validation
            formlyRoot.validators = { validation: [template] };
            // rootValidation.push( template );
          } else {
            // Else apply at the fieldTo level
            fieldTo.validators = {
              validation: [template],
            };
          }
        }
      } catch (err) {
        this.logger.warn('validation exeception:' + err);
      }
    }

    fieldGroupPrimitive.fieldGroup = [fieldFrom, fieldTo, fieldDays];

    // Wrapper For field
    fieldGroupPrimitive.wrappers = ['field'];

    config.push(fieldGroupPrimitive);
  }
}
